import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../../../../assets/locale/messages";
import {
  assignUserTagRequest,
  removeUserTagRequest,
} from "../../../../../../store/UserTags/actions";
import { showSuccessMsg } from "../../../../../../utils/Helpers";
import { getTagEligibility } from "../../tagEligibility";

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, value),
    template ?? "",
  );

const useApplicantTags = ({
  rows,
  selectedIds,
  rowsForBulkTag,
  portalVariant,
  myCompanyId,
  listParams,
  refreshList,
  setSelectedIds,
  setSelectedRowsById,
}) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const copy = messages[lang].events.viewEvents.applicantsTab.joiningRequests;
  const rowsForBulk = rowsForBulkTag ?? rows;

  const clearSelection = () => {
    setSelectedIds?.([]);
    setSelectedRowsById?.({});
  };

  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [tagTargetIds, setTagTargetIds] = useState([]);
  const [isBulkTag, setIsBulkTag] = useState(false);
  const [deleteTagTargetIds, setDeleteTagTargetIds] = useState([]);

  const resolveApplicants = (ids) => {
    const byId = new Map(rowsForBulk.map((row) => [String(row.id), row]));
    if (selectedApplicant) byId.set(String(selectedApplicant.id), selectedApplicant);
    return ids.map((id) => byId.get(String(id))).filter(Boolean);
  };

  const openApplicantTag = (row) => {
    setSelectedApplicant(row);
    setTagTargetIds([row.id]);
    setIsBulkTag(false);
  };

  const openBulkTag = () => {
    const firstSelectedRow = rowsForBulk.find((row) =>
      selectedIds.includes(row.id),
    );
    setSelectedApplicant(firstSelectedRow || null);
    setTagTargetIds(selectedIds);
    setIsBulkTag(true);
  };

  const closeApplicantTag = () => {
    setSelectedApplicant(null);
    setTagTargetIds([]);
    setIsBulkTag(false);
  };

  const saveTag = (tagId) => {
    const targetIds = tagTargetIds.length
      ? tagTargetIds
      : [selectedApplicant?.id].filter(Boolean);
    const applicants = resolveApplicants(targetIds);

    let userIds;
    if (targetIds.length === 1) {
      // Single-row edit: always send this user (add or replace own tag)
      const row = applicants[0];
      userIds = row?.userId ? [row.userId] : [];
    } else {
      const { eligibleUserIds } = getTagEligibility(applicants, {
        portalVariant,
        myCompanyId,
        mode: "assign",
      });
      userIds = eligibleUserIds;
    }

    const wasBulk = isBulkTag;
    closeApplicantTag();

    if (!userIds.length) return;

    dispatch(
      assignUserTagRequest({
        portalVariant,
        userIds,
        tagId,
        onSuccess: () => {
          showSuccessMsg(
            fill(copy.tagModal.assignSuccess, { count: userIds.length }),
            true,
          );
          if (wasBulk) clearSelection();
          refreshList?.(listParams);
        },
      }),
    );
  };

  const openDeleteBulkTag = () => {
    setDeleteTagTargetIds(selectedIds);
  };

  const closeDeleteTag = () => setDeleteTagTargetIds([]);

  const confirmDeleteTag = () => {
    const applicants = resolveApplicants(deleteTagTargetIds);
    const { eligibleUserIds } = getTagEligibility(applicants, {
      portalVariant,
      myCompanyId,
      mode: "remove",
    });

    closeDeleteTag();

    if (!eligibleUserIds.length) return;

    dispatch(
      removeUserTagRequest({
        portalVariant,
        userIds: eligibleUserIds,
        onSuccess: () => {
          showSuccessMsg(
            fill(copy.deleteTagModal.deleteSuccess, {
              count: eligibleUserIds.length,
            }),
            true,
          );
          clearSelection();
          refreshList?.(listParams);
        },
      }),
    );
  };

  return {
    selectedApplicant,
    tagTargetIds,
    isBulkTag,
    deleteTagTargetIds,
    openApplicantTag,
    openBulkTag,
    saveTag,
    openDeleteBulkTag,
    closeApplicantTag,
    closeDeleteTag,
    confirmDeleteTag,
  };
};

export default useApplicantTags;
